import React, { useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "./ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./ui/select";

const TYPES = [
    { value: "other", label: "Other" },
    { value: "allocated_bay", label: "Allocated bay" },
    { value: "visitor_bay", label: "Visitor bay" },
    { value: "communal_road", label: "Communal road" },
    { value: "communal_walk", label: "Communal walkway" },
    { value: "communal_carpark", label: "Communal car park" },
];

export default function SuggestLocationDialog({ mapBlobUrl, open, onOpenChange, onSubmitted }) {
    const containerRef = useRef(null);
    const [pin, setPin] = useState(null);
    const [form, setForm] = useState({ label: "", type: "other", description: "" });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) {
            setPin(null);
            setForm({ label: "", type: "other", description: "" });
        }
    }, [open]);

    const onMapClick = (e) => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        setPin({ x, y });
    };

    const submit = async (e) => {
        e.preventDefault();
        if (mapBlobUrl && !pin) {
            toast.error("Click on the map to place a pin first.");
            return;
        }
        if (!form.label.trim()) {
            toast.error("Please describe the location with a short label.");
            return;
        }
        setSubmitting(true);
        try {
            const res = await api.post("/location-suggestions", {
                label: form.label.trim(),
                type: form.type,
                description: form.description.trim() || null,
                coords_x: pin ? parseFloat(pin.x.toFixed(2)) : null,
                coords_y: pin ? parseFloat(pin.y.toFixed(2)) : null,
            });
            toast.success("Suggestion sent for admin review");
            onSubmitted?.(res.data);
            onOpenChange?.(false);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to submit suggestion");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl" data-testid="suggest-location-dialog">
                <DialogHeader>
                    <DialogTitle>Suggest a new location</DialogTitle>
                    <DialogDescription>
                        Add the missing parking bay or communal area. If the estate map is available, drop a pin; otherwise an estate admin will place it during review.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                    {mapBlobUrl ? (
                        <div
                            ref={containerRef}
                            onClick={onMapClick}
                            className="relative border border-slate-200 rounded-lg overflow-hidden cursor-crosshair"
                            data-testid="suggest-map-container"
                        >
                            <img src={mapBlobUrl} alt="Estate layout" className="w-full h-auto block select-none" draggable={false} />
                            {pin && (
                                <div
                                    className="absolute -translate-x-1/2 -translate-y-full text-rose-600 pointer-events-none"
                                    style={{ left: `${pin.x}%`, top: `${pin.y}%` }}
                                    data-testid="suggest-map-pin"
                                >
                                    <MapPin size={20} fill="currentColor" />
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="border border-dashed border-slate-200 rounded-lg px-4 py-6 text-sm text-slate-500" data-testid="suggest-no-map">
                            The estate map is not available yet. Submit a clear description and an admin will assess and place it on the map.
                        </div>
                    )}
                    <div className="text-xs text-slate-500" data-testid="suggest-pin-readout">
                        {pin
                            ? `Pin placed at ${pin.x.toFixed(1)}%, ${pin.y.toFixed(1)}% - admins will review before it appears for everyone.`
                            : (mapBlobUrl ? "Click the map to drop a pin." : "No map is available, so a description is enough.")}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label>Label</Label>
                            <Input
                                value={form.label}
                                onChange={(e) => setForm({ ...form, label: e.target.value })}
                                placeholder="e.g. Delivery bay near Block B"
                                required
                                data-testid="suggest-label-input"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Type</Label>
                            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                                <SelectTrigger data-testid="suggest-type-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label>Description (optional)</Label>
                        <Textarea
                            rows={2}
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            placeholder="Why this location matters, signage notes, nearby bay markings, etc."
                            data-testid="suggest-description-input"
                        />
                    </div>

                    <DialogFooter>
                        <Button
                            type="submit"
                            disabled={submitting}
                            className="bg-slate-700 hover:bg-slate-800 text-white"
                            data-testid="submit-suggestion-button"
                        >
                            {submitting ? "Submitting..." : "Submit for review"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
